import {
  CheckoutElementsProvider,
  ContactDetailsElement,
  PaymentElement,
  ShippingAddressElement,
  useCheckoutElements,
} from "@stripe/react-stripe-js/checkout";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { CheckoutSessionResponse } from "@neus/shared";
import { api, checkoutProblems } from "../api/client";
import { appearance, stripePromise } from "../components/payment/stripe";
import { toCartLines, useCart } from "../store/cart";
import styles from "./pages.module.css";

/**
 * Order summary rendered from the Checkout Session itself, so subtotal, shipping and
 * Stripe Tax always match what the customer is charged.
 */
function OrderSummary({ requiresShipping }: { requiresShipping: boolean }) {
  const state = useCheckoutElements();
  if (state.type !== "success") {
    return (
      <aside className={styles.summary}>
        <h2>Order Summary</h2>
        <p className="muted">{state.type === "error" ? state.error.message : "Loading…"}</p>
      </aside>
    );
  }
  const { lineItems, total, tax, shipping } = state.checkout;
  const pendingAddress = requiresShipping ? "Enter shipping address" : "Enter billing address";

  return (
    <aside className={styles.summary}>
      <h2>Order Summary</h2>
      <ul className={styles.summaryLines}>
        {lineItems.map((l) => (
          <li key={l.id}>
            <span>
              {l.name} <span className="muted">× {l.quantity}</span>
            </span>
            <span>{l.subtotal.amount}</span>
          </li>
        ))}
      </ul>
      <div className={styles.summaryRow}>
        <span>Subtotal</span>
        <span>{total.subtotal.amount}</span>
      </div>
      {requiresShipping && (
        <div className={styles.summaryRow}>
          <span>Shipping</span>
          <span>
            {!shipping ? (
              <span className="muted">{pendingAddress}</span>
            ) : total.shippingRate.minorUnitsAmount === 0 ? (
              "Free"
            ) : (
              total.shippingRate.amount
            )}
          </span>
        </div>
      )}
      <div className={styles.summaryRow}>
        <span>Tax</span>
        <span>
          {tax.status === "ready" ? total.taxExclusive.amount : <span className="muted">{pendingAddress}</span>}
        </span>
      </div>
      <div className={`${styles.summaryRow} ${styles.summaryTotal}`}>
        <span>Total</span>
        <strong>{total.total.amount}</strong>
      </div>
      <Link to="/cart" style={{ fontSize: "0.9rem" }}>
        Edit cart
      </Link>
    </aside>
  );
}

function CheckoutForm({ requiresShipping }: { requiresShipping: boolean }) {
  const state = useCheckoutElements();
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (state.type !== "success" || submitting) return;
    setSubmitting(true);
    setMessage(null);
    // Validates the elements, then redirects to the session's return_url on success.
    const result = await state.checkout.confirm();
    if (result.type === "error") setMessage(result.error.message);
    setSubmitting(false);
  };

  return (
    <form onSubmit={onSubmit} className={styles.checkoutForm}>
      <h2>Contact</h2>
      <ContactDetailsElement />
      {requiresShipping && (
        <>
          <h2>Shipping address</h2>
          <ShippingAddressElement />
        </>
      )}
      <h2>Payment</h2>
      <PaymentElement options={{ layout: "tabs" }} />
      {message && (
        <div className="notice notice--warn" role="alert" style={{ marginTop: 16 }}>
          {message}
        </div>
      )}
      <button
        type="submit"
        className="btn btn--lg btn--block"
        disabled={state.type !== "success" || submitting}
        style={{ marginTop: 20 }}
      >
        {submitting
          ? "Processing…"
          : state.type === "success"
            ? `Pay ${state.checkout.total.total.amount}`
            : "Loading…"}
      </button>
      <p className="muted" style={{ fontSize: "0.8rem", textAlign: "center" }}>
        Payments and sales tax are securely processed by Stripe. Card details never touch our servers.
      </p>
    </form>
  );
}

export function CheckoutPage() {
  const items = useCart((s) => s.items);
  const navigate = useNavigate();
  const [session, setSession] = useState<CheckoutSessionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const linesKey = JSON.stringify(toCartLines(items));

  // A new Checkout Session per cart: the server reprices from the catalog and checks stock.
  useEffect(() => {
    if (!stripePromise) return;
    const lines = toCartLines(items);
    if (lines.length === 0) {
      navigate("/cart", { replace: true });
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await api.createCheckoutSession({ lines });
        if (cancelled) return;
        setSession(res);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        const problems = checkoutProblems(err);
        if (problems) navigate("/cart", { replace: true, state: { problems } });
        else setError(err instanceof Error ? err.message : "Checkout is unavailable right now.");
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linesKey]);

  return (
    <>
      <div className="page-header">
        <div className="container">
          <h1>Checkout</h1>
        </div>
      </div>
      <div className="container section">
        {!stripePromise ? (
          <div className="notice notice--warn">
            Online payments aren&rsquo;t configured. Set <code>VITE_STRIPE_PUBLISHABLE_KEY</code> in <code>.env</code>.
          </div>
        ) : error ? (
          <div className="notice notice--warn" role="alert">
            {error} <Link to="/cart">Return to cart</Link>
          </div>
        ) : !session ? (
          <p>Preparing secure checkout…</p>
        ) : (
          <CheckoutElementsProvider
            key={session.clientSecret}
            stripe={stripePromise}
            options={{ clientSecret: session.clientSecret, elementsOptions: { appearance: appearance() } }}
          >
            <div className={styles.cartLayout}>
              <CheckoutForm requiresShipping={session.requiresShipping} />
              <OrderSummary requiresShipping={session.requiresShipping} />
            </div>
          </CheckoutElementsProvider>
        )}
      </div>
    </>
  );
}
